'use client';
import { useEffect, useState } from 'react';
import PalletConfig from './pallet-config';
import { db } from '@/services/Database';
import { useDispatch, useSelector } from 'react-redux';
import { setPalletTypes } from '@/redux/slices/pallet/palletSlice';
import { showSuccessToast, showErrorToast } from "@/components/toast/toast";
import type { PalletType } from '@/redux/slices/pallet/palletSlice';
import type { Pallet, ShippingMode } from '@/services/Database';
import { RootState } from '@/redux/store';

interface OfflinePalletConfigProps {
  projectId?: string;
  isOffline?: boolean;
}

// Helper function to convert DB Pallet to Redux PalletType
export const convertToPalletType = (pallet: Pallet & { shippingModes?: ShippingMode[] }): PalletType => ({
  id: pallet.id,
  projectId: pallet.projectId,
  variant: pallet.variant,
  name: pallet.name,
  mode: pallet.mode || 'default',
  numberOfPallets: pallet.numberOfPallets,
  percentage: pallet.percentage || 0,
  plusByUser: pallet.plusByUser,
  stackTestLayoutWeek: pallet.stackTestLayoutWeek,
  stackPattern: pallet.stackPattern,
  createdAt: pallet.createdAt,
  updatedAt: pallet.updatedAt,
  lpnNumbers: [],
  shippingModes: pallet.shippingModes || []
});

export default function OfflinePalletConfig({ projectId, isOffline }: OfflinePalletConfigProps) {
  const [isLoading, setIsLoading] = useState(true);
  const dispatch = useDispatch();
  const { activePallet, palletTypes } = useSelector((state: RootState) => state.pallets);
  // Load initial pallet data
  const getOfflinePalletDetails =async(palletId:number)=>await db.getPalletDetails(palletId)

  useEffect(() => {
    const loadPalletData = async () => {
      try {
        if (projectId) {
          try {
            setIsLoading(true);
            const response = await db.getAllPalletsForProject(parseInt(projectId));
            console.log(response,'response')
            // Even if no pallets found, we should still initialize with empty array
            const pallets = response?.pallets || [];
            const convertedPallets = pallets.map(convertToPalletType);
            dispatch(setPalletTypes(convertedPallets));

          } catch (error) {
            console.error('Error loading pallet data:', error);
            showErrorToast('Failed to load pallet configuration');
            dispatch(setPalletTypes([]));
          } finally {
            setIsLoading(false);
          }
        } else {
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error loading pallet data:', error);
      }
    };

    loadPalletData();
  }, [projectId, dispatch]);

  // Handle AddPalletDialog submission
  const handleAddPallet = async (values: any) => {
    try {
      console.log('Add pallet values:', values,projectId);
      const pallet = await db.generatePallet({
        type: 'create',
        palletDetails: {
          projectId: Number(projectId),
          variant: values.palletDetails.variant,
          name: values.palletDetails.name,
          numberOfPallets: Number(values.palletDetails.numberOfPallets)
        },
        shippingModeIds: values.shippingModeIds || []
      });

      if (pallet) {
        // After creating a new pallet, refresh the list
        const response = await db.getAllPalletsForProject(Number(projectId));
        console.log(response,'response')
        const pallets = response?.pallets || [];
        const convertedPallets = pallets.map(convertToPalletType);
        dispatch(setPalletTypes(convertedPallets));
        showSuccessToast("New pallet created successfully");
      }
    } catch (error) {
      console.error('Error creating pallet:', error);
      showErrorToast("Failed to create pallet");
    }
  };

  // Handle formik form submission
  const handleFormSubmit = async (values: any) => {
    try {
      const dataToSend = {
        ...values,
        secondaryPackagingDetails: values.secondaryPackagingDetails.secondaryPkg === "true"
          ? values.secondaryPackagingDetails
          : { secondaryPkg: false },
        tertiaryPackagingDetails: values.tertiaryPackagingDetails.tertiaryPkg === "true"
          ? values.tertiaryPackagingDetails
          : { tertiaryPkg: false },
      };

      await db.createPalletDetails({
        type: values.type,
        palletId: values.palletId,
        primaryPackagingDetails: dataToSend.primaryPackagingDetails,
        secondaryPackagingDetails: dataToSend.secondaryPackagingDetails,
        tertiaryPackagingDetails: dataToSend.tertiaryPackagingDetails,
        palletDetails: dataToSend.palletDetails,
        unitLoadDetails: dataToSend.unitLoadDetails,
        palletizingRecipe: dataToSend.palletizingRecipe
      });

      const response = await db.getAllPalletsForProject(Number(projectId));
      const convertedPallets = response.pallets.map(convertToPalletType);
      dispatch(setPalletTypes(convertedPallets));
      showSuccessToast("Pallet details saved successfully");
    } catch (error) {
      console.error('Error saving pallet details:', error);
      showErrorToast("Failed to save pallet details");
    }
  };

  // Handle pallet update (variant, name, etc.)
  const handleUpdatePallet = async (values: any) => {
    try {
      const pallet = await db.generatePallet({
        type: 'update',
        palletId: values.id,
        palletDetails: {
          variant: values.variant,
          name: values.name,
          numberOfPallets: Number(values.numberOfPallets),
          projectId: Number(projectId)
        },
        shippingModeIds: values.shippingModes.map((sm: any) => sm.id)
      });

      if (pallet) {
        const response = await db.getAllPalletsForProject(Number(projectId));
        const convertedPallets = response.pallets.map(convertToPalletType);
        dispatch(setPalletTypes(convertedPallets));
        showSuccessToast("Pallet updated successfully");
      }
    } catch (error) {
      console.error('Error updating pallet:', error);
      showErrorToast("Failed to update pallet");
    }
  };

  const handleOfflineSubmit = async (values: any) => {
    console.log('Offline submit values:', values);

    if (values.type === 'create') {
      await handleAddPallet(values);
    } else if (values.type === 'update') {
      await handleFormSubmit(values);
    } else if (values.type === 'updatePallet') {
      await handleUpdatePallet(values);
    }
  };

  if (isLoading) {
    return <div>Loading pallet configuration...</div>;
  }
console.log(palletTypes,'palletTypes')
  return (
    <div className="w-full">
      <PalletConfig
        projectId={projectId?.toString() || ''}
        isOffline={isOffline}
        onOfflineSubmit={handleOfflineSubmit}
        getOfflinePalletDetails={getOfflinePalletDetails}
      />
      {/* Show empty state message within the table structure */}
      {palletTypes.length === 0 && (
        <div className="w-full flex flex-col gap-4 items-center justify-center p-8">
          <div className="text-2xl font-semibold text-gray-500">No Pallets Found</div>
          <div className="text-blue font-semibold">
            Click on &quot;Add&quot; button on the side bar to add a new Pallet
          </div>
        </div>
      )}
    </div>
  );
}
